import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { AreaTexto, Aviso, Botao, Carregando, Entrada, Modal } from '../../componentes/ui.jsx';
import { ModalPaciente, resumoDoPaciente } from '../contatos/FormPaciente.jsx';
import { ROTULO_TIPO } from './tiposServico.js';
import './assistente.css';

/**
 * Marcar horario numa tela so.
 *
 * A esquerda, as quatro escolhas em ordem (paciente -> servico -> medico ->
 * dia e horario); cada uma aparece quando a anterior esta feita e o que tem
 * uma resposta so ja vem escolhido. A direita, o resumo fixo com pagamento,
 * observacoes e o botao de confirmar — que diz o que falta.
 *
 * Os dias mostram quantas vagas tem (GET /api/agenda/dias-livres): a recepcao
 * nao precisa testar dia por dia ate achar horario.
 *
 * Trocar uma escolha limpa as que dependem dela (servico novo = medico e hora
 * de novo).
 */

/** Servico que a IA so pre-agenda (cirurgia/bariatrica ou marcado no catalogo). Pela tela vale direto. */
const exigeConfirmacao = (s) => Boolean(s?.exigeConfirmacao) || s?.tipo === 'cirurgia' || s?.tipo === 'bariatrica';

const ORDEM_TIPO = ['consulta', 'retorno', 'bariatrica', 'exame', 'procedimento', 'cirurgia'];
const JANELA_DIAS = 14;
const DIA_CURTO = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const hojeIso = () => new Date().toLocaleDateString('sv-SE');
const somarDias = (iso, n) => {
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(a, m - 1, d + n)).toISOString().slice(0, 10);
};
const partes = (iso) => {
  const [a, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(a, m - 1, d));
  return { dia: d, mes: MES_CURTO[m - 1], semana: DIA_CURTO[dt.getUTCDay()] };
};
function dataPorExtenso(iso) {
  const [a, m, d] = iso.split('-').map(Number);
  const t = new Date(a, m - 1, d).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' });
  return t.charAt(0).toUpperCase() + t.slice(1);
}
const inicial = (nome) => (nome ?? '?').trim().charAt(0).toUpperCase();

/** Cabecalho numerado de cada escolha: numero vira ✓ quando esta feita. */
function Secao({ numero, titulo, feito, ativo, acao, children }) {
  return (
    <section className={`ag-secao${ativo ? '' : ' ag-secao--espera'}`} aria-disabled={!ativo}>
      <header className="ag-secao__topo">
        <span className={`ag-secao__num${feito ? ' ag-secao__num--feito' : ''}`} aria-hidden="true">
          {feito ? '✓' : numero}
        </span>
        <h3 className="ag-secao__titulo">{titulo}</h3>
        {acao}
      </header>
      {ativo && <div className="ag-secao__corpo">{children}</div>}
    </section>
  );
}

/**
 * @param {object} p
 * @param {string} p.dataInicial
 * @param {object} [p.clienteInicial]  veio da ficha do contato: ja comeca escolhido.
 */
export function AssistenteAgendar({ dataInicial, clienteInicial = null, aoFechar, aoSalvar }) {
  const [cliente, setCliente] = useState(clienteInicial);
  const [paciente, setPaciente] = useState(null);
  const [cadastrandoPaciente, setCadastrandoPaciente] = useState(false);
  const [pagamento, setPagamento] = useState('particular');
  const [convenio, setConvenio] = useState('');
  const [carteirinha, setCarteirinha] = useState('');
  const [servico, setServico] = useState(null);
  const [profissional, setProfissional] = useState(null);
  const [inicioJanela, setInicioJanela] = useState(() => (dataInicial && dataInicial > hojeIso() ? dataInicial : hojeIso()));
  const [data, setData] = useState('');
  const [hora, setHora] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [busca, setBusca] = useState('');
  const [buscaAplicada, setBuscaAplicada] = useState('');
  const fimRef = useRef(null);
  const diasRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setBuscaAplicada(busca.trim()), 250);
    return () => clearTimeout(t);
  }, [busca]);

  const contatos = useQuery({
    queryKey: ['leads', 'busca-agendar', buscaAplicada],
    queryFn: () => api.get('/api/leads', { busca: buscaAplicada, limite: 6 }),
    enabled: !cliente,
    placeholderData: (anterior) => anterior
  });
  const pacientes = useQuery({
    queryKey: ['pacientes', cliente?.id],
    queryFn: () => api.get(`/api/leads/${cliente.id}/pacientes`),
    enabled: Boolean(cliente)
  });
  const listaPacientes = pacientes.data?.pacientes ?? [];
  const servicos = useQuery({ queryKey: ['servicos'], queryFn: () => api.get('/api/servicos') });

  // Uma resposta so: ja vem escolhida.
  useEffect(() => {
    if (!paciente && listaPacientes.length === 1) setPaciente(listaPacientes[0]);
  }, [listaPacientes.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const profs = servico?.profissionais ?? [];
    if (servico && !profissional && profs.length === 1) setProfissional(profs[0]);
  }, [servico?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trocou de paciente: o convenio sugerido acompanha (continua editavel).
  useEffect(() => {
    setConvenio(paciente?.convenio ?? '');
    setCarteirinha(paciente?.carteirinha ?? '');
    setPagamento(paciente?.convenio ? 'convenio' : 'particular');
  }, [paciente?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dias = useQuery({
    // A forma de pagamento escolhe as faixas: convenio = ordem de chegada, particular = horario marcado.
    queryKey: ['dias-livres', servico?.id, profissional?.id, inicioJanela, pagamento],
    queryFn: () =>
      api.get('/api/agenda/dias-livres', { de: inicioJanela, dias: JANELA_DIAS, serviceId: servico.id, professionalId: profissional.id, pagamento }),
    enabled: Boolean(servico && profissional)
  });
  // Primeiro dia com vaga ja fica escolhido (a pessoa so troca se quiser).
  useEffect(() => {
    if (!dias.data || (data && dias.data.dias.some((d) => d.data === data))) return;
    const primeiro = dias.data.dias.find((d) => d.livres > 0);
    setData(primeiro?.data ?? '');
    setHora('');
  }, [dias.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const horarios = useQuery({
    queryKey: ['horarios', servico?.id, profissional?.id, data, pagamento],
    queryFn: () => api.get('/api/agenda/horarios-livres', { data, serviceId: servico.id, professionalId: profissional.id, pagamento }),
    enabled: Boolean(servico && profissional && data)
  });
  // Convenio por ordem de chegada: uma opcao por faixa ("a partir das 08:00").
  const chegadas = (horarios.data?.horarios ?? []).filter((h) => h.chegada);
  const chegadaEscolhida = chegadas.find((h) => h.hora === hora);
  const turnos = useMemo(() => {
    const lista = (horarios.data?.horarios ?? []).filter((h) => !h.chegada);
    return [
      ['Manhã', lista.filter((h) => h.hora < '12:00')],
      ['Tarde', lista.filter((h) => h.hora >= '12:00')]
    ].filter(([, l]) => l.length);
  }, [horarios.data]);

  const gruposServico = useMemo(() => {
    const lista = (servicos.data?.servicos ?? []).filter((s) => s.ativo !== false);
    const porTipo = new Map();
    for (const s of lista) {
      const t = ORDEM_TIPO.includes(s.tipo) ? s.tipo : 'consulta';
      if (!porTipo.has(t)) porTipo.set(t, []);
      porTipo.get(t).push(s);
    }
    return ORDEM_TIPO.filter((t) => porTipo.has(t)).map((t) => [t, porTipo.get(t)]);
  }, [servicos.data]);

  const criar = useMutation({ mutationFn: (dados) => api.post('/api/agenda', dados), onSuccess: aoSalvar });

  // Ao concluir uma escolha, a proxima aparece: rola ate ela.
  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [paciente?.id, servico?.id, profissional?.id]);

  /**
   * Setas da faixa de dias: primeiro rolam o que ja esta carregado (cabem ~8
   * de 14); so na ponta trocam a janela — senao os dias do meio sumiam.
   */
  function rolarDias(sentido) {
    const el = diasRef.current;
    if (!el) return;
    const noFim = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    const noInicio = el.scrollLeft <= 4;
    if (sentido > 0 && noFim) {
      setInicioJanela((d) => somarDias(d, JANELA_DIAS));
      el.scrollTo({ left: 0 });
    } else if (sentido < 0 && noInicio) {
      if (inicioJanela <= hojeIso()) return;
      setInicioJanela((d) => {
        const n = somarDias(d, -JANELA_DIAS);
        return n < hojeIso() ? hojeIso() : n;
      });
    } else {
      el.scrollBy({ left: sentido * (el.clientWidth - 40), behavior: 'smooth' });
    }
  }

  function escolherCliente(c) {
    setCliente(c);
    setPaciente(null);
  }
  function trocarCliente() {
    setCliente(null);
    setPaciente(null);
    setBusca('');
  }
  function escolherServico(s) {
    if (servico?.id === s.id) return;
    setServico(s);
    setProfissional(null);
    setData('');
    setHora('');
  }
  function escolherProfissional(p) {
    if (profissional?.id === p.id) return;
    setProfissional(p);
    setData('');
    setHora('');
  }

  const precoTexto = pagamento === 'convenio' ? 'Convênio' : profissional?.precoFormatado ?? servico?.precoFormatado ?? '—';
  const faltando = !cliente ? 'o paciente' : !paciente ? 'o paciente' : !servico ? 'o serviço' : !profissional ? 'o médico' : !hora ? 'o horário' : null;

  function confirmar() {
    if (faltando) return;
    criar.mutate({
      leadId: cliente.id,
      pacienteId: paciente.id,
      pagamento,
      convenio: pagamento === 'convenio' ? convenio.trim() || undefined : undefined,
      carteirinha: pagamento === 'convenio' ? carteirinha.trim() || undefined : undefined,
      serviceId: servico.id,
      professionalId: profissional.id,
      data,
      hora,
      observacoes: observacoes.trim() || undefined
    });
  }

  const listaContatos = contatos.data?.itens ?? [];

  return (
    <Modal titulo="Marcar horário" aberto aoFechar={aoFechar} largura={1040} className="modal--agendar">
      <div className="ag">
        <div className="ag__escolhas">
          {/* 1. PACIENTE ------------------------------------------------------ */}
          <Secao
            numero={1}
            titulo="Paciente"
            feito={Boolean(paciente)}
            ativo
            acao={
              cliente && (
                <button type="button" className="ag-link" onClick={trocarCliente}>
                  Trocar contato
                </button>
              )
            }
          >
            {!cliente ? (
              <>
                <div className="ag-busca">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" />
                    <path d="M20 20l-3.5-3.5" />
                  </svg>
                  <Entrada
                    autoFocus
                    placeholder="Buscar contato por nome ou telefone"
                    aria-label="Buscar contato"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && listaContatos[0]) escolherCliente(listaContatos[0]);
                    }}
                  />
                </div>
                <div className="ag-lista" role="list">
                  {contatos.isLoading && <Carregando texto="Buscando..." />}
                  {listaContatos.map((c) => (
                    <button key={c.id} type="button" role="listitem" className="ag-linha" onClick={() => escolherCliente(c)}>
                      <span className="ag-avatar" aria-hidden="true">{inicial(c.nome)}</span>
                      <span className="ag-linha__texto">
                        <strong>{c.nome}</strong>
                        <small>{c.telefoneFormatado}</small>
                      </span>
                    </button>
                  ))}
                  {contatos.data && listaContatos.length === 0 && <p className="ag-vazio">Nenhum contato com esse nome ou telefone.</p>}
                </div>
              </>
            ) : (
              <>
                <div className="ag-contato">
                  <span className="ag-avatar" aria-hidden="true">{inicial(cliente.nome)}</span>
                  <span className="ag-linha__texto">
                    <strong>{cliente.nome}</strong>
                    <small>{cliente.telefoneFormatado}</small>
                  </span>
                </div>
                {pacientes.isLoading && <Carregando texto="Buscando os pacientes..." />}
                {pacientes.isError && <Aviso tom="alerta">{pacientes.error.message}</Aviso>}
                {pacientes.data && (
                  <>
                    <p className="ag-rotulo">{listaPacientes.length ? 'Quem será atendido?' : 'Este contato ainda não tem paciente cadastrado.'}</p>
                    <div className="ag-chips">
                      {listaPacientes.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          aria-pressed={paciente?.id === p.id}
                          className={`ag-chip${paciente?.id === p.id ? ' ag-chip--sel' : ''}`}
                          onClick={() => setPaciente(p)}
                          title={resumoDoPaciente(p)}
                        >
                          {p.nomeSocial || p.nome}
                          {p.convenio && <span className="ag-chip__tag">{p.convenio}</span>}
                        </button>
                      ))}
                      <button type="button" className="ag-chip ag-chip--novo" onClick={() => setCadastrandoPaciente(true)}>
                        + Novo paciente
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </Secao>

          {/* 2. SERVICO ------------------------------------------------------- */}
          <Secao numero={2} titulo="Serviço" feito={Boolean(servico)} ativo={Boolean(paciente)}>
            {servicos.isLoading && <Carregando texto="Carregando serviços..." />}
            <div className="ag-servicos">
              {gruposServico.map(([tipo, lista]) => (
                <div key={tipo} className="ag-grupo">
                  <p className="ag-rotulo">{ROTULO_TIPO[tipo] ?? 'Consulta'}</p>
                  <div className="ag-lista ag-lista--servicos">
                    {lista.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        aria-pressed={servico?.id === s.id}
                        className={`ag-servico${servico?.id === s.id ? ' ag-servico--sel' : ''}`}
                        onClick={() => escolherServico(s)}
                      >
                        <span className="ag-radio" aria-hidden="true" />
                        <span className="ag-servico__nome">{s.nome}</span>
                        <span className="ag-servico__meta">
                          {s.duracaoMinutos} min · {s.precoFormatado}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {servico && exigeConfirmacao(servico) && (
              <p className="ag-nota">Pela IA este serviço só entra como pré-agendamento; marcado aqui pela equipe, já vale direto.</p>
            )}
            {servico?.tipo === 'retorno' && <p className="ag-nota">Retorno é gratuito dentro do prazo do médico.</p>}
          </Secao>

          {/* 3. MEDICO -------------------------------------------------------- */}
          <Secao numero={3} titulo="Médico" feito={Boolean(profissional)} ativo={Boolean(servico)}>
            {(servico?.profissionais ?? []).length === 0 ? (
              <Aviso tom="alerta">Nenhum profissional realiza este serviço. Vincule um em Equipe.</Aviso>
            ) : (
              <div className="ag-chips">
                {servico.profissionais.map((p) => {
                  const diferente = p.precoFormatado !== servico.precoFormatado || p.duracaoMinutos !== servico.duracaoMinutos;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      aria-pressed={profissional?.id === p.id}
                      className={`ag-chip ag-chip--pessoa${profissional?.id === p.id ? ' ag-chip--sel' : ''}`}
                      onClick={() => escolherProfissional(p)}
                    >
                      <span className="ag-avatar ag-avatar--p" aria-hidden="true">{inicial(p.nome.replace(/^Dra?\.\s*/, ''))}</span>
                      {p.nome}
                      {diferente && <span className="ag-chip__tag">{p.duracaoMinutos} min · {p.precoFormatado}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </Secao>

          {/* 4. DIA E HORARIO ------------------------------------------------- */}
          <Secao
            numero={4}
            titulo="Dia e horário"
            feito={Boolean(hora)}
            ativo={Boolean(profissional)}
            acao={
              profissional && (
                <div className="ag-outra-data">
                  <Entrada
                    type="date"
                    required
                    aria-label="Ir para outra data"
                    min={hojeIso()}
                    value={inicioJanela}
                    onChange={(e) => {
                      setInicioJanela(e.target.value);
                      setData(e.target.value);
                      setHora('');
                    }}
                  />
                </div>
              )
            }
          >
            <p className="ag-modo">
              {pagamento === 'convenio' ? (
                <>
                  Agenda de <strong>convênio</strong>: ordem de chegada.
                </>
              ) : (
                <>
                  Agenda <strong>particular</strong>: horário marcado.
                </>
              )}{' '}
              <span className="texto-fraco">Troque em Pagamento, no resumo.</span>
            </p>
            <div className="ag-dias-nav">
              <button type="button" className="ag-seta" aria-label="Dias anteriores" onClick={() => rolarDias(-1)}>
                ‹
              </button>
              <div className="ag-dias" role="list" ref={diasRef}>
                {dias.isLoading &&
                  Array.from({ length: 7 }, (_, i) => <span key={i} className="ag-dia ag-dia--esqueleto" aria-hidden="true" />)}
                {(dias.data?.dias ?? []).map((d) => {
                  const p = partes(d.data);
                  return (
                    <button
                      key={d.data}
                      type="button"
                      role="listitem"
                      disabled={!d.livres}
                      aria-pressed={data === d.data}
                      aria-label={`${dataPorExtenso(d.data)}: ${d.livres ? `${d.livres} horários livres` : 'sem vaga'}`}
                      className={`ag-dia${data === d.data ? ' ag-dia--sel' : ''}`}
                      onClick={() => {
                        setData(d.data);
                        setHora('');
                      }}
                    >
                      <span className="ag-dia__semana">{d.data === hojeIso() ? 'hoje' : p.semana}</span>
                      <span className="ag-dia__num">{p.dia}</span>
                      <span className="ag-dia__mes">{p.mes}</span>
                      <span className="ag-dia__vagas">
                        {!d.livres ? '—' : d.chegada ? 'chegada' : `${d.livres} vaga${d.livres === 1 ? '' : 's'}`}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button type="button" className="ag-seta" aria-label="Próximos dias" onClick={() => rolarDias(1)}>
                ›
              </button>
            </div>
            {dias.isError && <Aviso tom="alerta">{dias.error.message}</Aviso>}
            {dias.data && !dias.data.dias.some((d) => d.livres) && (
              <p className="ag-vazio">
                {profissional?.nome} não tem vaga nestes {JANELA_DIAS} dias. Veja os próximos dias (›) ou outra data.
              </p>
            )}

            {data && (
              <div className="ag-horas">
                {horarios.isLoading && <Carregando texto="Consultando a agenda..." />}
                {horarios.isError && <Aviso tom="alerta">{horarios.error.message}</Aviso>}
                {horarios.data && turnos.length === 0 && chegadas.length === 0 && <p className="ag-vazio">Sem horário livre em {dataPorExtenso(data)}.</p>}
                {chegadas.map((h) => (
                  <button
                    key={h.hora}
                    type="button"
                    aria-pressed={hora === h.hora}
                    className={`ag-chegada${hora === h.hora ? ' ag-chegada--sel' : ''}`}
                    onClick={() => setHora(h.hora)}
                  >
                    <strong>Ordem de chegada</strong>
                    <span>
                      a partir das {h.hora}
                      {h.ate ? ` · até ${h.ate}` : ''}
                    </span>
                  </button>
                ))}
                {turnos.map(([nome, lista]) => (
                  <div key={nome} className="ag-turno">
                    <p className="ag-rotulo">{nome}</p>
                    <div className="ag-horas__grade">
                      {lista.map((h) => (
                        <button
                          key={h.hora}
                          type="button"
                          aria-pressed={hora === h.hora}
                          className={`ag-hora${hora === h.hora ? ' ag-hora--sel' : ''}`}
                          onClick={() => setHora(h.hora)}
                        >
                          {h.hora}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Secao>
          <div ref={fimRef} />
        </div>

        {/* RESUMO ------------------------------------------------------------- */}
        <aside className="ag__resumo" aria-label="Resumo do agendamento">
          <h3 className="ag-resumo__titulo">Resumo</h3>
          <dl className="ag-resumo__itens">
            <div>
              <dt>Paciente</dt>
              <dd>{paciente ? paciente.nomeSocial || paciente.nome : <span className="ag-falta">—</span>}</dd>
            </div>
            <div>
              <dt>Serviço</dt>
              <dd>{servico ? servico.nome : <span className="ag-falta">—</span>}</dd>
            </div>
            <div>
              <dt>Médico</dt>
              <dd>{profissional ? profissional.nome : <span className="ag-falta">—</span>}</dd>
            </div>
            <div>
              <dt>Quando</dt>
              <dd>
                {hora ? (
                  <>
                    {dataPorExtenso(data)}
                    <small>
                      {chegadaEscolhida
                        ? `ordem de chegada, a partir das ${hora}`
                        : `às ${hora} · ${profissional?.duracaoMinutos ?? servico?.duracaoMinutos} min`}
                    </small>
                  </>
                ) : (
                  <span className="ag-falta">—</span>
                )}
              </dd>
            </div>
          </dl>

          <div className="ag-resumo__bloco">
            <p className="ag-rotulo">Pagamento</p>
            <div className="ag-segmento" role="radiogroup" aria-label="Pagamento">
              {[
                ['particular', 'Particular'],
                ['convenio', 'Convênio']
              ].map(([v, r]) => (
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={pagamento === v}
                  className={pagamento === v ? 'ag-segmento--sel' : ''}
                  onClick={() => {
                    if (pagamento === v) return;
                    // Muda as faixas da agenda: o horario escolhido deixa de valer.
                    setPagamento(v);
                    setData('');
                    setHora('');
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
            {pagamento === 'convenio' && (
              <div className="ag-convenio">
                <Entrada placeholder="Convênio" aria-label="Convênio" value={convenio} maxLength={80} onChange={(e) => setConvenio(e.target.value)} />
                <Entrada placeholder="Carteirinha" aria-label="Carteirinha" value={carteirinha} maxLength={40} onChange={(e) => setCarteirinha(e.target.value)} />
              </div>
            )}
          </div>

          <div className="ag-resumo__bloco">
            <p className="ag-rotulo">Observações</p>
            <AreaTexto rows={2} placeholder="Opcional" value={observacoes} maxLength={500} onChange={(e) => setObservacoes(e.target.value)} />
          </div>

          <div className="ag-total">
            <span>Total</span>
            <strong>{servico ? precoTexto : '—'}</strong>
          </div>

          {criar.isError && <Aviso tom="perigo">{criar.error.message}</Aviso>}
          <Botao className="ag-confirmar" carregando={criar.isPending} disabled={Boolean(faltando)} onClick={confirmar}>
            Confirmar agendamento
          </Botao>
          <p className="ag-dica">{faltando ? `Falta escolher ${faltando}.` : 'Tudo certo. Confira e confirme.'}</p>
        </aside>
      </div>

      {cadastrandoPaciente && (
        <ModalPaciente
          leadId={cliente.id}
          nomeContato={cliente.nome}
          primeiro={listaPacientes.length === 0}
          aoFechar={() => setCadastrandoPaciente(false)}
          aoSalvar={(novo) => {
            // O recem-cadastrado ja fica escolhido e a lista se atualiza.
            setCadastrandoPaciente(false);
            setPaciente(novo);
            pacientes.refetch();
          }}
        />
      )}
    </Modal>
  );
}
